import React, { useEffect, useRef, useState, useCallback } from 'react';
import mermaid from 'mermaid';
import { v4 as uuidv4 } from 'uuid';

// Initialize mermaid with some sensible defaults that match the app's styling
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'Poppins, sans-serif',
  themeVariables: {
    primaryColor: '#f3f4f6',
    primaryTextColor: '#374151',
    primaryBorderColor: '#d1d5db',
    lineColor: '#9ca3af',
    secondaryColor: '#9860A7', // Kaleido purple
    tertiaryColor: '#263A95',  // Kaleido blue
  }
});

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const ZOOM_FACTOR = 1.03;

export const MermaidDiagram = ({ chart }) => {
  const containerRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState(null);
  const [id] = useState(`mermaid-${uuidv4()}`);
  const [expanded, setExpanded] = useState(false);

  // Use refs for zoom/pan so wheel handler always sees latest values
  const zoomRef = useRef(1);
  const [zoom, setZoomState] = useState(1);
  const setZoom = useCallback((val) => {
    const next = typeof val === 'function' ? val(zoomRef.current) : val;
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    zoomRef.current = clamped;
    setZoomState(clamped);
  }, []);

  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panOrigin = useRef({ x: 0, y: 0 });

  // Wheel listener cleanup ref
  const wheelCleanupRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const renderDiagram = async () => {
      if (!chart) return;

      try {
        setError(null);
        setSvgContent('');

        // Sanitize common LLM artifacts from mermaid code
        let sanitizedChart = chart
          // Truncate at inline closing fence (e.g. "class Value value;```\nsome prose...")
          .replace(/```[\s\S]*$/, '')
          // Remove standalone markdown code fence lines
          .replace(/^```(?:mermaid)?\s*$/gm, '')
          // Remove inline comments from graph declaration line
          .replace(/^(\s*(?:graph|flowchart)\s+(?:TD|TB|BT|RL|LR))\s+%%.*$/m, '$1')
          .trim();

        const { svg } = await mermaid.render(id, sanitizedChart);

        if (isMounted) {
          setSvgContent(svg);
        }
      } catch (err) {
        console.error('Failed to render Mermaid diagram:', err);
        if (isMounted) {
          setError(err.message || 'Failed to render diagram');
        }
      }
    };

    renderDiagram();

    return () => {
      isMounted = false;
    };
  }, [chart, id]);

  // Reset zoom/pan when toggling expanded
  useEffect(() => {
    zoomRef.current = 1;
    setZoomState(1);
    setPan({ x: 0, y: 0 });
  }, [expanded]);

  // Lock body scroll when expanded
  useEffect(() => {
    if (expanded) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [expanded]);

  // Callback ref for the viewport — attaches non-passive wheel listener directly on mount
  const viewportCallbackRef = useCallback((el) => {
    // Clean up previous listener
    if (wheelCleanupRef.current) {
      wheelCleanupRef.current();
      wheelCleanupRef.current = null;
    }

    if (!el) return;

    let lastZoomTime = 0;
    const COOLDOWN_MS = 150; // ignore wheel events for 150ms after each zoom

    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const now = performance.now();
      if (now - lastZoomTime < COOLDOWN_MS) return;
      lastZoomTime = now;

      const direction = Math.sign(e.deltaY);
      const factor = 1 - direction * 0.05;
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoomRef.current * factor));
      zoomRef.current = next;
      setZoomState(next);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    wheelCleanupRef.current = () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Clean up wheel listener on unmount
  useEffect(() => {
    return () => {
      if (wheelCleanupRef.current) wheelCleanupRef.current();
    };
  }, []);

  const handlePointerDown = useCallback((e) => {
    isPanning.current = true;
    panStart.current = { x: e.clientX, y: e.clientY };
    panOrigin.current = { x: pan.x, y: pan.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [pan]);

  const handlePointerMove = useCallback((e) => {
    if (!isPanning.current) return;
    setPan({
      x: panOrigin.current.x + (e.clientX - panStart.current.x),
      y: panOrigin.current.y + (e.clientY - panStart.current.y),
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    isPanning.current = false;
  }, []);

  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [setZoom]);

  // Keyboard support for expanded modal
  useEffect(() => {
    if (!expanded) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setExpanded(false);
      if (e.key === '+' || e.key === '=') setZoom(z => z * ZOOM_FACTOR);
      if (e.key === '-') setZoom(z => z / ZOOM_FACTOR);
      if (e.key === '0') resetView();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [expanded, resetView, setZoom]);

  if (error) {
    return (
      <div className="my-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 font-mono overflow-x-auto">
        <div className="font-semibold mb-2">Mermaid Syntax Error:</div>
        <pre>{error}</pre>
        <div className="mt-4 font-semibold">Original Code:</div>
        <pre className="mt-2 text-gray-700">{chart}</pre>
      </div>
    );
  }

  const diagramContent = svgContent ? (
    <div
      ref={containerRef}
      className="mermaid-container w-full flex justify-center"
      style={expanded ? {
        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
        transformOrigin: 'center center',
        cursor: isPanning.current ? 'grabbing' : 'grab',
      } : undefined}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  ) : (
    <div className="flex items-center justify-center h-32 text-kaleido-gray-400">
      <div className="w-5 h-5 border-2 border-kaleido-purple/20 border-t-kaleido-purple rounded-full animate-spin mr-2" />
      Rendering diagram...
    </div>
  );

  // Collapsed inline view
  if (!expanded) {
    return (
      <div className="my-6 flex flex-col w-full bg-white rounded-xl border border-kaleido-purple/10 shadow-sm">
        <div className="flex justify-end p-2 border-b border-gray-100">
          <button
            onClick={() => setExpanded(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-kaleido-purple hover:bg-gray-50 rounded-md transition-colors"
            title="Expand diagram"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
            </svg>
            Expand
          </button>
        </div>
        <div className="p-4 overflow-x-auto flex justify-center">
          {diagramContent}
        </div>
      </div>
    );
  }

  // Expanded fullscreen modal with zoom & pan
  return (
    <>
      {/* Placeholder to preserve layout */}
      <div className="my-6 h-16 flex items-center justify-center w-full bg-gray-50 rounded-xl border border-dashed border-gray-300 text-sm text-gray-400">
        Diagram expanded — click overlay to close
      </div>

      {/* Fullscreen overlay */}
      <div
        className="fixed inset-0 z-50 flex flex-col"
        style={{ backgroundColor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(4px)' }}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 bg-white/95 border-b border-gray-200 shadow-sm">
          <span className="text-sm font-medium text-gray-600">
            {Math.round(zoom * 100)}%
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setZoom(z => z / ZOOM_FACTOR)}
              className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              title="Zoom out (−)"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            <button
              onClick={() => setZoom(z => z * ZOOM_FACTOR)}
              className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              title="Zoom in (+)"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            <button
              onClick={resetView}
              className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              title="Reset view (0)"
            >
              Reset
            </button>
            <div className="w-px h-5 bg-gray-200 mx-1" />
            <button
              onClick={() => setExpanded(false)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Close (Esc)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Close
            </button>
          </div>
        </div>

        {/* Zoomable/pannable viewport */}
        <div
          ref={viewportCallbackRef}
          className="flex-1 overflow-hidden flex items-center justify-center"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{ touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
        >
          {diagramContent}
        </div>

        {/* Hint bar */}
        <div className="px-4 py-1.5 bg-white/90 border-t border-gray-200 text-xs text-gray-400 text-center">
          Scroll to zoom · Drag to pan · <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-500">Esc</kbd> to close · <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-500">0</kbd> to reset
        </div>
      </div>
    </>
  );
};
